import React, { useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Control, useController } from 'react-hook-form';
import ProductSelectionModal from '../../ProductSelectionModal';
import { ProductSelectionMode, ProductSelectionItem } from '../../../types';

interface ProductSelectorFieldProps {
  control: Control<any>;
  name: string;
  label: string;
  helperText?: TextFieldProps['helperText'];
  error?: boolean;
  selectionMode?: ProductSelectionMode;
  initialSelection?: ProductSelectionItem | ProductSelectionItem[] | null;
}

const ProductSelectorField: React.FC<ProductSelectorFieldProps> = ({
  control,
  name,
  label,
  helperText,
  error,
  selectionMode = 'single',
  initialSelection,
}) => {
  const { field } = useController({ name, control });
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<ProductSelectionItem[]>([]);
  const hasUserInteractedRef = React.useRef(false);
  const previousInitialIdsRef = React.useRef<string[]>([]);

  const normalizedInitial = useMemo(() => {
    if (!initialSelection) {
      return [] as ProductSelectionItem[];
    }
    if (Array.isArray(initialSelection)) {
      return initialSelection.filter(
        (item): item is ProductSelectionItem => Boolean(item)
      );
    }
    return [initialSelection];
  }, [initialSelection]);

  useEffect(() => {
    const ids = normalizedInitial.map((item) => item.id);
    const previousIds = previousInitialIdsRef.current;
    const hasChangedInitial =
      ids.length !== previousIds.length || ids.some((id, index) => id !== previousIds[index]);

    if (hasChangedInitial) {
      previousInitialIdsRef.current = ids;
      hasUserInteractedRef.current = false;
    }

    if (!ids.length || hasUserInteractedRef.current) {
      return;
    }

    if (selectionMode === 'multiple') {
      const currentIds = Array.isArray(field.value) ? field.value : [];
      const differs =
        currentIds.length !== ids.length || ids.some((id, index) => id !== currentIds[index]);
      if (differs) {
        field.onChange(ids);
      }
    } else {
      const currentId = Array.isArray(field.value) ? field.value[0] : field.value;
      if (currentId !== ids[0]) {
        field.onChange(ids[0] ?? '');
      }
    }
    setSelected(normalizedInitial);
  }, [field, normalizedInitial, selectionMode]);

  useEffect(() => {
    if (selectionMode === 'multiple') {
      if (Array.isArray(field.value)) {
        const currentIds = selected.map((item) => item.id).sort();
        const fieldIds = [...field.value].sort();
        if (
          currentIds.length !== fieldIds.length ||
          currentIds.some((id, index) => id !== fieldIds[index])
        ) {
          setSelected([]);
        }
      } else if (!field.value) {
        setSelected([]);
      }
    } else {
      const value = Array.isArray(field.value) ? field.value[0] : field.value;
      if (!value) {
        setSelected([]);
      } else if (!selected.some((item) => item.id === value)) {
        setSelected([]);
      }
    }
  }, [field.value, selected, selectionMode]);

  const displayValue = useMemo(() => {
    if (selected.length) {
      return selected.map((item) => item.name).join(', ');
    }
    if (Array.isArray(field.value)) {
      return field.value.join(', ');
    }
    return field.value ?? '';
  }, [field.value, selected]);

  const selectedIds = useMemo(() => {
    if (selected.length) {
      return selected.map((item) => item.id);
    }
    if (Array.isArray(field.value)) {
      return field.value;
    }
    if (field.value) {
      return [field.value];
    }
    return [];
  }, [field.value, selected]);

  const handleConfirm = (products: ProductSelectionItem[]) => {
    hasUserInteractedRef.current = true;
    if (selectionMode === 'multiple') {
      const ids = products.map((product) => product.id);
      field.onChange(ids);
      setSelected(products);
    } else {
      const product = products[0];
      field.onChange(product ? product.id : '');
      setSelected(product ? [product] : []);
    }
  };

  const handleClear = () => {
    hasUserInteractedRef.current = true;
    setSelected([]);
    field.onChange(selectionMode === 'multiple' ? [] : '');
  };

  return (
    <>
      <TextField
        label={label}
        value={displayValue}
        onClick={() => setModalOpen(true)}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {displayValue && (
                <Tooltip title="Effacer">
                  <span>
                    <IconButton onClick={handleClear} size="small">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Rechercher">
                <IconButton onClick={() => setModalOpen(true)} size="small">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        fullWidth
        helperText={helperText}
        error={error}
      />
      <ProductSelectionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        selectionMode={selectionMode}
        selectedIds={selectedIds}
      />
    </>
  );
};

export default ProductSelectorField;
